import classNames from 'classnames';
import { ReactElement, RefObject } from 'react';
import { useRef } from 'react';
import styles from './LogoIntroSection.module.scss';
import {
  setupTransitionInTimeline,
  timelineInOut,
  TRANSITION_IN_DURATION,
} from './LogoIntroSection.transitions';
import type { LogoIntroSectionProps } from './LogoIntroSection.data';
import Heading from '../../../atoms/heading/Heading';
import Text from '../../../atoms/text/Text';
import useScrollTrigger from '@/hooks/useScrollTrigger';
import useSetupTimeline from '@/hooks/useSetupTimeline';
import { useScrollTransition } from 'util/react-transition-component';

export default function LogoIntroSection({
  title,
  kanjiText,
  className,
  ref,
}: LogoIntroSectionProps): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const iconRef = useRef<HTMLDivElement>(null);
  const logoTextRef = useRef<HTMLParagraphElement>(null);
  const leftLogoRef = useRef<SVGPathElement>(null);
  const rightLogoRef = useRef<SVGPathElement>(null);
  const kanjiTextRef = useRef<HTMLParagraphElement>(null);
  const tlRef = useRef<GSAPTimeline>(undefined);

  tlRef.current = useSetupTimeline(timelineInOut, {
    logoTextRef,
    kanjiTextRef,
    leftLogoRef,
    rightLogoRef,
  });

  useScrollTransition({
    scroll: {
      trigger: elementRef,
      disableTriggerInOnScroll: true,
      vars: {},
    },
    setupOptions: () => ({
      ref: elementRef,
      refs: {
        tlRef,
        elementRef,
      },
      setupTransitionInTimeline,
    }),
  });

  useScrollTrigger({
    trigger: elementRef,
    pin: elementRef,
    vars: {
      scrub: true,
      start: 'top top',
      end: '50%',
      onUpdate: ({ progress }) => {
        tlRef.current?.progress(TRANSITION_IN_DURATION + progress * (1 - TRANSITION_IN_DURATION));
      },
      onLeaveBack: () => {
        tlRef.current?.progress(TRANSITION_IN_DURATION);
      },
    },
  });

  return (
    <div ref={elementRef} className={classNames(styles.logoIntroSection, className)}>
      <div className={classNames(styles.logoHolder)}>
        <div ref={iconRef} className={classNames(styles.logo)}>
          <svg viewBox="0 0 488 310" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M0 276.55C0 276.55 39.9908 276.55 77 276.55C144.379 276.55 199 221.929 199 154.55C199 87.1711 144.379 32.5498 77 32.5498C39.9909 32.5498 0 32.5498 0 32.5498"
              ref={leftLogoRef}
              stroke="currentColor"
              strokeWidth="67"
            />
            <path
              d="M488 32.5498C488 32.5498 448.009 32.5498 411 32.5498C343.621 32.5498 289 87.1711 289 154.55C289 221.929 343.621 276.55 411 276.55C448.009 276.55 488 276.55 488 276.55"
              ref={rightLogoRef}
              stroke="currentColor"
              strokeWidth="67"
            />
          </svg>
        </div>

        <div className={classNames(styles.textHolder)}>
          <Heading
            ref={logoTextRef}
            transitionInType="splitCharsSlideInUp"
            transitionOutType="splitCharsSlideOutUp"
            className={classNames(styles.logoText)}
            as="h1"
            size="heading-05"
          >
            {title}
          </Heading>

          <Text
            ref={kanjiTextRef}
            transitionInType="splitCharsSlideInUp"
            transitionOutType="splitCharsSlideOutUp"
            className={classNames(styles.kanjiText)}
            as="p"
            size="kanji-large"
            lang="ja"
          >
            {kanjiText}
          </Text>
        </div>
      </div>
    </div>
  );
}
